/**
 * 🔐 Secure Token Storage for Coach Cedar
 * Platform-aware token management for authentication
 * 
 * Web: Uses localStorage (via storage service)
 * Native: Uses expo-secure-store (encrypted keychain/keystore)
 */

import * as SecureStore from 'expo-secure-store';
import { Platform, NativeModules } from 'react-native';
import { storage } from './storage';
import { noteTokenIssued } from '../auth/staleCredentialDetector';
// '@/specs/…' (not relative): vite aliases these two specifiers to a null stub on
// web, where react-native-web has no TurboModuleRegistry. Native + jest resolve
// the real specs through the generic '@' alias.
import NativeWatchAuthRelayModule from '@/specs/NativeWatchAuthRelayModule';
import NativeWearAuthRelayModule from '@/specs/NativeWearAuthRelayModule';

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  USER_DATA: 'auth_user_data',
  CEDAR_JWT: 'cedar_jwt', // Coach Cedar backend JWT
} as const;

/**
 * Every credential is saved AFTER_FIRST_UNLOCK. expo-secure-store's default is
 * WHEN_UNLOCKED, which the keychain refuses to read while the phone is locked —
 * and iOS starts Cedar with the phone locked (HealthKit delivery, a background
 * refresh), so a boot could not see a token it had. kirsten.brouse 044901ed,
 * 2026-10-14: sent to sign in with a valid session, and her workout logged in kg.
 * AFTER_FIRST_UNLOCK keeps the items encrypted from a restart until the first
 * unlock; only the locked-after-that window opens. Android ignores the option.
 */
const KEYCHAIN_OPTIONS: SecureStore.SecureStoreOptions = {
  keychainAccessible: SecureStore.AFTER_FIRST_UNLOCK,
};

/**
 * A secure read has three answers. `unreadable` (the keychain refused) is not
 * `absent` (nothing stored): a caller that treats the first as the second signs
 * out someone whose credentials are intact.
 */
export type SecureRead<T> =
  | { state: 'present'; value: T }
  | { state: 'absent' }
  | { state: 'unreadable'; errorType: string };

/** The error's code or class name — never its message. */
function errorTypeOf(error: unknown): string {
  const e = error as { code?: unknown; name?: unknown } | null;
  return String(e?.code ?? e?.name ?? 'unknown');
}

/**
 * Saving over an existing keychain item changes its value and keeps its
 * accessibility (expo-secure-store update()), so an older build's WHEN_UNLOCKED
 * items stay unreadable while locked until they are re-created. The move writes
 * a backup copy first and removes it last, so a move stopped between the delete
 * and the write still has the credential (readSecureItem restores it).
 */
const MIGRATION_BACKUP_SUFFIX = '__accessibility_move';
const ACCESSIBILITY_MIGRATION_FLAG = 'secure_store_accessibility';
const ACCESSIBILITY_MIGRATION_DONE = 'after_first_unlock';
const MIGRATED_KEYS = [
  TOKEN_KEYS.ACCESS_TOKEN,
  TOKEN_KEYS.REFRESH_TOKEN,
  TOKEN_KEYS.CEDAR_JWT,
  TOKEN_KEYS.USER_DATA,
] as const;

/**
 * Every keychain WRITE runs one at a time. The accessibility move reads a value
 * and writes it back; a token refresh saving a rotated refresh token in between
 * would otherwise be overwritten by the spent one, and Auth0 refuses a spent
 * refresh token — a sign-out.
 */
let _keychainWriteChain: Promise<unknown> = Promise.resolve();
function withKeychainWriteLock<T>(fn: () => Promise<T>): Promise<T> {
  const run = _keychainWriteChain.then(fn, fn);
  _keychainWriteChain = run.catch(() => undefined);
  return run;
}

/** Inside the write lock only: the stored value, or a stopped move's backup put back. */
async function readOrRestoreLocked(key: string): Promise<string | null> {
  const value = await SecureStore.getItemAsync(key);
  if (value) return value;
  const backupKey = key + MIGRATION_BACKUP_SUFFIX;
  const backup = await SecureStore.getItemAsync(backupKey);
  if (!backup) return null;
  await SecureStore.setItemAsync(key, backup, KEYCHAIN_OPTIONS);
  await SecureStore.deleteItemAsync(backupKey);
  console.warn(`⚠️ KEYCHAIN_ACCESSIBILITY_MOVE_RESTORED key=${key}`);
  return backup;
}

async function readSecureItem(key: string): Promise<SecureRead<string>> {
  try {
    if (Platform.OS === 'web') {
      const value = await storage.getItem(key);
      return value ? { state: 'present', value } : { state: 'absent' };
    }
    const value = await SecureStore.getItemAsync(key);
    if (value) return { state: 'present', value };
    if (!(await SecureStore.getItemAsync(key + MIGRATION_BACKUP_SUFFIX))) return { state: 'absent' };
    const restored = await withKeychainWriteLock(() => readOrRestoreLocked(key));
    return restored ? { state: 'present', value: restored } : { state: 'absent' };
  } catch (error) {
    return { state: 'unreadable', errorType: errorTypeOf(error) };
  }
}

async function readSecureItemOrNull(key: string, marker: string): Promise<string | null> {
  const read = await readSecureItem(key);
  if (read.state === 'unreadable') {
    console.error(`❌ ${marker} error=${read.errorType}`);
    return null;
  }
  return read.state === 'present' ? read.value : null;
}

let _migrationInFlight: Promise<KeychainMigrationResult> | null = null;

export type KeychainMigrationResult = 'migrated' | 'already' | 'unreadable' | 'failed';

async function migrateKeychainAccessibilityOnce(): Promise<KeychainMigrationResult> {
  // Keychain accessibility is iOS only; Android ignores the option, so there is nothing to move.
  if (Platform.OS !== 'ios') return 'already';
  if ((await storage.getItem(ACCESSIBILITY_MIGRATION_FLAG)) === ACCESSIBILITY_MIGRATION_DONE) {
    return 'already';
  }
  return withKeychainWriteLock(async () => {
    // Read and rewrite inside the lock, so no save can land between them.
    const values: Array<[string, string]> = [];
    try {
      for (const key of MIGRATED_KEYS) {
        const value = await readOrRestoreLocked(key);
        if (value) values.push([key, value]);
      }
    } catch {
      return 'unreadable' as const;
    }
    for (const [key, value] of values) {
      const backupKey = key + MIGRATION_BACKUP_SUFFIX;
      try {
        await SecureStore.setItemAsync(backupKey, value, KEYCHAIN_OPTIONS);
        await SecureStore.deleteItemAsync(key);
        await SecureStore.setItemAsync(key, value, KEYCHAIN_OPTIONS);
        await SecureStore.deleteItemAsync(backupKey);
      } catch (error) {
        console.error(`❌ KEYCHAIN_ACCESSIBILITY_MOVE_FAILED key=${key} error=${errorTypeOf(error)}`);
        return 'failed' as const;
      }
    }
    await storage.setItem(ACCESSIBILITY_MIGRATION_FLAG, ACCESSIBILITY_MIGRATION_DONE);
    console.log('KEYCHAIN_ACCESSIBILITY_MOVED');
    return 'migrated' as const;
  });
}

// 🔒 Short-lived in-memory cache for getCedarJwt to avoid hammering
// SecureStore / AsyncStorage on every poll tick (notification 5s + subscription 10s
// + pending response 3s). Cache lives for 5 seconds — long enough to deduplicate
// concurrent callers, short enough that token rotations are picked up promptly.
let _jwtCache: { token: string | null; expiry: number } | null = null;
const JWT_CACHE_TTL_MS = 5000;

/**
 * Told when `clearAll` has removed the session. Every sign-out in the app —
 * the entry route's, AuthContext's, Settings', the provider's own — goes
 * through `clearAll`, and before 2026-10-14 nothing heard it: the native Auth0
 * provider kept saying "signed in" for a session another reader had just
 * ended, so the login screen it was sent to bounced straight back to the
 * entry route. The provider listens here as it listens to TokenManager for a
 * refresh: its state follows the stored session both ways.
 */
const _clearedListeners = new Set<() => void>();

/**
 * Write the legacy `jwt_token` mirror, and on failure REMOVE it rather than
 * leaving whatever was there before.
 *
 * Why removal beats leaving it (2026-08-14): the canonical key is written
 * first and this mirror second, as two separate awaits. If the mirror throws
 * after the canonical succeeded, the two diverge — canonical fresh, mirror
 * EXPIRED — and every hand-reader of `jwt_token` then sends a dead token on
 * every request while TokenManager reports the refresh succeeded. A whole
 * session of 401s with nothing in the logs saying why.
 *
 * A MISSING mirror fails closed (readers fall through to the canonical read or
 * surface a clean "no token"); a STALE mirror fails open. So on any write
 * failure we delete. The removal is itself guarded — if storage is hard down
 * we cannot do better, and saveTokens' outer catch still rethrows so the
 * caller learns the write was not clean.
 */
async function writeLegacyJwtMirror(cedarJwt: string): Promise<void> {
  try {
    await storage.setItem('jwt_token', cedarJwt);
  } catch (e) {
    console.warn('⚠️ JWT_MIRROR_WRITE_FAILED — removing stale legacy jwt_token:', e);
    try {
      await storage.removeItem('jwt_token');
    } catch (removeErr) {
      console.error('❌ JWT_MIRROR_REMOVE_FAILED — legacy jwt_token may be stale:', removeErr);
    }
    throw e;
  }
}

export const secureTokenStorage = {
  /**
   * Save authentication tokens (Auth0 + Cedar JWT)
   */
  saveTokens(accessToken: string, refreshToken?: string, cedarJwt?: string): Promise<void> {
    return withKeychainWriteLock(async () => {
      try {
        if (__DEV__) {
          console.log('💾 saveTokens:', accessToken ? '✓' : '✗', 'access,', refreshToken ? '✓' : '✗', 'refresh,', cedarJwt ? '✓' : '✗', 'jwt');
        }
      
        if (Platform.OS === 'web') {
          // Web: Use storage service (localStorage wrapper)
          await storage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
          if (refreshToken) {
            await storage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
          }
          if (cedarJwt) {
            await storage.setItem(TOKEN_KEYS.CEDAR_JWT, cedarJwt);
            await writeLegacyJwtMirror(cedarJwt);
            _jwtCache = { token: cedarJwt, expiry: Date.now() + JWT_CACHE_TTL_MS }; // Warm cache immediately
            if (__DEV__) console.log('✅ Cedar JWT saved to storage (web)');
          } else {
            if (__DEV__) console.log('⚠️ No Cedar JWT provided to save');
          }
        } else {
          // Native: Use SecureStore (encrypted)
          await SecureStore.setItemAsync(TOKEN_KEYS.ACCESS_TOKEN, accessToken, KEYCHAIN_OPTIONS);
          if (refreshToken) {
            await SecureStore.setItemAsync(TOKEN_KEYS.REFRESH_TOKEN, refreshToken, KEYCHAIN_OPTIONS);
          }
          if (cedarJwt) {
            await SecureStore.setItemAsync(TOKEN_KEYS.CEDAR_JWT, cedarJwt, KEYCHAIN_OPTIONS);
            _jwtCache = { token: cedarJwt, expiry: Date.now() + JWT_CACHE_TTL_MS }; // Warm cache immediately
            // Mirror to unified storage so DataService/useMyPathLogic (jwt_token) get the token
            await writeLegacyJwtMirror(cedarJwt);
            // 🔧 M35 FIX: Bridge JWT to Kotlin SharedPreferences so CoachCedarApiClient
            // can authenticate Health Connect sync API calls. Without this, all Kotlin-side
            // API calls fail silently with null auth token.
            if (Platform.OS === 'android' && NativeModules.HealthConnectModule) {
              try {
                await NativeModules.HealthConnectModule.setAuthToken(cedarJwt);
              } catch (e) {
                console.warn('⚠️ Failed to bridge JWT to HealthConnectModule:', e);
              }
            }
            // Relay the JWT to the Apple Watch app over WCSession so the watch can
            // authenticate /api/watch/sync + completions per-user (replaces the removed
            // baked token). No-op if no paired watch / module unavailable.
            // Phase 3: TurboModule specs (null when the platform lacks the module).
            if (Platform.OS === 'ios' && NativeWatchAuthRelayModule) {
              try {
                await NativeWatchAuthRelayModule.relayToken(cedarJwt);
              } catch (e) {
                console.warn('⚠️ Failed to relay JWT to Apple Watch:', e);
              }
            }
            // Same relay for the Wear OS companion over the Wearable Data Layer (M0). The
            // Android module mirrors the iOS one's relayToken/clearToken promise API.
            if (Platform.OS === 'android' && NativeWearAuthRelayModule) {
              try {
                await NativeWearAuthRelayModule.relayToken(cedarJwt);
              } catch (e) {
                console.warn('⚠️ Failed to relay JWT to Wear OS watch:', e);
              }
            }
            if (__DEV__) console.log('✅ Cedar JWT saved to SecureStore + storage (native)');
          } else {
            if (__DEV__) console.log('⚠️ No Cedar JWT provided to save');
          }
        }
      
        if (__DEV__) console.log('✅ Tokens saved securely');
      } catch (error) {
        console.error('❌ Failed to save tokens:', error);
        throw error;
      }
    });
  },

  /**
   * Get Auth0 access token
   */
  async getAccessToken(): Promise<string | null> {
    return readSecureItemOrNull(TOKEN_KEYS.ACCESS_TOKEN, 'ACCESS_TOKEN_UNREADABLE');
  },

  /** The Auth0 access token, saying whether the keychain refused the read. */
  async readAccessToken(): Promise<SecureRead<string>> {
    return readSecureItem(TOKEN_KEYS.ACCESS_TOKEN);
  },

  /**
   * Get Auth0 refresh token
   */
  async getRefreshToken(): Promise<string | null> {
    return readSecureItemOrNull(TOKEN_KEYS.REFRESH_TOKEN, 'REFRESH_TOKEN_UNREADABLE');
  },

  /**
   * The Coach Cedar backend JWT, saying whether the keychain refused the read.
   * Boot code reads this, never getCedarJwt: only `absent` means signed out.
   */
  async readCedarJwt(): Promise<SecureRead<string>> {
    // Return cached token if still fresh (avoids hammering SecureStore on every poll tick)
    if (_jwtCache && Date.now() < _jwtCache.expiry && _jwtCache.token) {
      // The 5s cache is itself a staleness seam — a refresh that landed
      // mid-TTL is invisible to this branch. Report, never block.
      noteTokenIssued(_jwtCache.token, 'storage_read');
      return { state: 'present', value: _jwtCache.token };
    }

    const read = await readSecureItem(TOKEN_KEYS.CEDAR_JWT);

    // 🔧 M35B FIX: Only cache non-null tokens. Caching null defeats the
    // pre-warm JWT retry loop (useChatLogic) because subsequent retries
    // hit the cached null for the entire 5s TTL window.
    if (read.state === 'present') {
      _jwtCache = { token: read.value, expiry: Date.now() + JWT_CACHE_TTL_MS };
      // Hand-readers of this method bypass TokenManager entirely, so a token
      // that predates the last successful refresh would be sent with nothing
      // recording it. Emits at most once per offending token (see the detector).
      noteTokenIssued(read.value, 'storage_read');
    } else if (__DEV__ && read.state === 'absent') {
      console.log('❌ getCedarJwt: No JWT token found');
    }
    return read;
  },

  /**
   * Get Coach Cedar backend JWT — `null` for both "none stored" and "the
   * keychain refused", for the many callers that only need a token to send.
   * The refusal is logged at ERROR (CEDAR_JWT_UNREADABLE).
   */
  async getCedarJwt(): Promise<string | null> {
    const read = await this.readCedarJwt();
    if (read.state === 'unreadable') {
      console.error(`❌ CEDAR_JWT_UNREADABLE error=${read.errorType}`);
      return null;
    }
    return read.state === 'present' ? read.value : null;
  },

  /**
   * Save user data from Auth0
   */
  saveUserData(userData: any): Promise<void> {
    return withKeychainWriteLock(async () => {
      try {
        const userDataString = JSON.stringify(userData);
      
        if (Platform.OS === 'web') {
          await storage.setItem(TOKEN_KEYS.USER_DATA, userDataString);
        } else {
          await SecureStore.setItemAsync(TOKEN_KEYS.USER_DATA, userDataString, KEYCHAIN_OPTIONS);
        }
      
        // 🚨 CRITICAL: Also save user_id to regular storage for useChatLogic
        if (userData.id) {
          await storage.setItem('user_id', userData.id);
          if (__DEV__) console.log('✅ User ID saved to regular storage');
        }
        if (userData.name) {
          await storage.setItem('user_name', userData.name);
        }
      
        if (__DEV__) console.log('✅ User data saved');
      } catch (error) {
        console.error('❌ Failed to save user data:', error);
        throw error;
      }
    });
  },

  /**
   * Get stored user data
   */
  async getUserData(): Promise<any | null> {
    const read = await this.readUserData();
    if (read.state === 'unreadable') {
      console.error(`❌ USER_DATA_UNREADABLE error=${read.errorType}`);
      return null;
    }
    return read.state === 'present' ? read.value : null;
  },

  /** The stored user record, saying whether the keychain refused the read. */
  async readUserData(): Promise<SecureRead<any>> {
    const read = await readSecureItem(TOKEN_KEYS.USER_DATA);
    if (read.state !== 'present') return read;
    try {
      return { state: 'present', value: JSON.parse(read.value) };
    } catch (error) {
      console.error(`❌ USER_DATA_UNPARSEABLE error=${errorTypeOf(error)}`);
      return { state: 'absent' };
    }
  },

  /**
   * Check if user has valid tokens
   */
  async hasValidTokens(): Promise<boolean> {
    const accessToken = await this.getAccessToken();
    const cedarJwt = await this.getCedarJwt();
    
    // Both tokens required for full authentication
    return !!(accessToken && cedarJwt);
  },

  /**
   * Clear all authentication data (logout)
   */
  clearAll(): Promise<void> {
    return withKeychainWriteLock(async () => {
      _jwtCache = null; // Invalidate cache on logout
      try {
        if (Platform.OS === 'web') {
          await storage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
          await storage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
          await storage.removeItem(TOKEN_KEYS.CEDAR_JWT);
          await storage.removeItem(TOKEN_KEYS.USER_DATA);
          await storage.removeItem('jwt_token');
        } else {
          for (const key of MIGRATED_KEYS) {
            await SecureStore.deleteItemAsync(key);
            // A move's backup would otherwise restore the session on the next read.
            await SecureStore.deleteItemAsync(key + MIGRATION_BACKUP_SUFFIX);
          }
          await storage.removeItem('jwt_token');
          // Clear Kotlin-side auth token on logout
          if (Platform.OS === 'android' && NativeModules.HealthConnectModule) {
            try {
              await NativeModules.HealthConnectModule.clearAuthToken();
            } catch (e) {
              console.warn('⚠️ Failed to clear Kotlin auth token:', e);
            }
          }
          // Wipe the token on the Apple Watch on logout (distinct authCleared signal so the
          // watch's ignore-empty guard doesn't keep a live token on the wrist).
          if (Platform.OS === 'ios' && NativeWatchAuthRelayModule) {
            try {
              await NativeWatchAuthRelayModule.clearToken();
            } catch (e) {
              console.warn('⚠️ Failed to clear Apple Watch auth token:', e);
            }
          }
          // Wear OS companion: the distinct `authCleared` DataItem wipes the wrist token.
          if (Platform.OS === 'android' && NativeWearAuthRelayModule) {
            try {
              await NativeWearAuthRelayModule.clearToken();
            } catch (e) {
              console.warn('⚠️ Failed to clear Wear OS watch auth token:', e);
            }
          }
        }

        // Clear user identity data from regular storage on both platforms
        await storage.removeItem('user_id');
        await storage.removeItem('user_name');

        if (__DEV__) console.log('✅ All auth data cleared');
      } catch (error) {
        console.error('❌ Failed to clear auth data:', error);
        throw error;
      }
      // Only a clear that finished: one that threw may have left the session.
      for (const listener of [..._clearedListeners]) {
        try {
          listener();
        } catch (e) {
          console.error('❌ AUTH_CLEARED_LISTENER_FAILED:', (e as Error)?.name ?? 'unknown');
        }
      }
    });
  },

  /** Hear every finished `clearAll`. Returns the way to stop listening. */
  onCleared(listener: () => void): () => void {
    _clearedListeners.add(listener);
    return () => { _clearedListeners.delete(listener); };
  },

  /**
   * Re-create an older build's WHEN_UNLOCKED credentials AFTER_FIRST_UNLOCK —
   * once per install, only when every item can be read. Single-flight.
   */
  async migrateKeychainAccessibility(): Promise<KeychainMigrationResult> {
    if (!_migrationInFlight) {
      _migrationInFlight = migrateKeychainAccessibilityOnce().finally(() => {
        _migrationInFlight = null;
      });
    }
    return _migrationInFlight;
  },
};

export function _resetSecureTokenStorageForTests(): void {
  _jwtCache = null;
  _migrationInFlight = null;
  _clearedListeners.clear();
}
